/**
 * 경보음 — 앱이 **열려 있을 때** 울리는 주의 신호.
 *
 * 푸시 알림은 OS 채널이 울리지만(`AlertChannels.java`), 앱을 보고 있는 동안 온 경보는
 * OS 알림이 아니라 오버레이(AlarmCenter)로 뜬다. 그때 음성 낭독만 있으면 첫 단어가
 * 나올 때까지 1~2초가 조용하고, TTS 가 실패한 기기(언어팩 없음·서버 폴백 꺼짐)는
 * **아예 무음**이다. 방송의 관례대로 **신호음 → 말**의 순서로 울린다.
 *
 * 파일은 푸시 채널과 **같은 것**(`public/sounds/gasm_alarm.wav`, `scripts/make-alarm-sound.py`)
 * — 어느 경로로 듣든 「이 소리 = 가스 경보」로 한 번에 학습된다.
 *
 * 실패는 조용히 삼킨다(브라우저 자동재생 차단·파일 누락) — 시각 경보와 음성이 남는다.
 * 대신 **항상 resolve** 해서 호출자가 뒤에 붙인 TTS 를 막지 않는다.
 */

const SRC = `${import.meta.env.BASE_URL}sounds/gasm_alarm.wav`;
/** 파일 길이 2.9초. 재생이 어디선가 멈춰도 이 이상 다음 단계를 붙들지 않는다. */
const MAX_WAIT_MS = 4000;

let current: HTMLAudioElement | null = null;
let inFlight: Promise<void> | null = null;
let finishCurrent: (() => void) | null = null;

/**
 * 재생 중이면 **같은 약속을 돌려준다**. 한 경보를 낭독기(AlertTtsAnnouncer)와
 * 오버레이(AlarmCenter)가 거의 동시에 보는데, 둘 다 「신호음 뒤에 말」을 원한다 —
 * 두 번째 호출이 첫 재생을 끊고 다시 시작하면 사이렌이 더듬는 것처럼 들린다.
 */
export function playAlarmTone(): Promise<void> {
  if (typeof Audio === "undefined") return Promise.resolve();
  if (inFlight) return inFlight;
  const p = new Promise<void>((resolve) => {
    const audio = new Audio(SRC);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (current === audio) current = null;
      if (finishCurrent === finish) finishCurrent = null;
      inFlight = null;
      resolve();
    };
    const timer = setTimeout(finish, MAX_WAIT_MS);
    audio.preload = "auto";
    audio.addEventListener("ended", finish, { once: true });
    audio.addEventListener("error", finish, { once: true });
    current = audio;
    finishCurrent = finish;
    audio.play().catch((e: unknown) => {
      console.warn("[alarmTone] play failed", e);
      finish();
    });
  });
  inFlight = p;
  return p;
}

/** 음성 정지 버튼과 같이 눌러 신호음도 멈춘다. 기다리던 말은 곧바로 풀려난다. */
export function stopAlarmTone(): void {
  if (current) {
    try {
      current.pause();
      current.currentTime = 0;
    } catch {
      /* noop */
    }
  }
  finishCurrent?.();
}
